import { parseEther, parseUnits } from 'ethers/lib/utils';
import { useWeb3React } from '@web3-react/core';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
import abis from '../../config/contracts/abis';
import { calculateMargin, fetcher } from '../../web3/utils';
import useTokenDetails from './useTokenDetails';
import { BigNumber, ethers } from 'ethers';
import useErrorState from '../../context/error/useErrorState';
import useModalState from '../../context/modal/useModalState';
import { APPROVING, Tokens } from '../../config/constants';
import { IERC20Metadata__factory } from '../../config/contracts/typechain';
import { verifyChainConfig } from '../../helpers';
import useToastState from '../../context/toast/useToastState';

const useApproveToken = (
    tokenAddress: string | undefined,
    spender: string | undefined,
    amount: string
) => {
    const { library, account, chainId } = useWeb3React();

    const { setError } = useErrorState();

    const { setToast } = useToastState();

    const { openModal, setModalData, closeModal } = useModalState();

    const { decimals } = useTokenDetails(tokenAddress);

    const [isApproved, setIsApproved] = useState(false);

    const { data: allowance, mutate } = useSWR<BigNumber>(
        library && account && tokenAddress && spender
            ? [tokenAddress, 'allowance', account, spender]
            : null,
        {
            fetcher: fetcher(library, abis.erc20),
            fallbackData: parseEther('0'),
            dedupingInterval: undefined,
            refreshInterval: undefined,
        }
    );

    useEffect(() => {
        if (!allowance || allowance.isZero()) {
            setIsApproved(false);
            return;
        }

        if (allowance.gte(parseUnits(amount || '0', decimals))) {
            setIsApproved(true);
            return;
        }

        setIsApproved(false);
    }, [allowance, amount, decimals]);

    const approve = async (approveAmount: string) => {
        try {
            if (!tokenAddress)
                throw new Error('useApproveToken: tokenAddress not specified');
            if (!spender) throw new Error('useApproveToken: spender not specified');
            if (!verifyChainConfig(chainId)) return;

            openModal('waitingModal');
            setModalData((prev) => ({ ...prev, status: APPROVING }));

            const erc20Contract = IERC20Metadata__factory.connect(
                tokenAddress,
                library.getSigner()
            );

            let useExact = false;

            const estimatedGas = await erc20Contract.estimateGas
                .approve(spender, ethers.constants.MaxUint256)
                .catch(() => {
                    // general fallback for tokens who restrict approval amounts
                    useExact = true;

                    return erc20Contract.estimateGas.approve(
                        spender,
                        parseUnits(approveAmount, decimals)
                    );
                });

            const approve = await erc20Contract.approve(
                spender,
                useExact
                    ? parseUnits(approveAmount, decimals)
                    : ethers.constants.MaxUint256,
                {
                    gasLimit: calculateMargin(estimatedGas),
                }
            );

            await approve.wait();

            await mutate();

            const approveMessage = `${Tokens.USDC} approved!`;

            setToast(
                {
                    type: 'success',
                    message: approveMessage,
                },
                approveMessage
            );
        } catch (error) {
            setError(error);
        } finally {
            closeModal('waitingModal');
        }
    };

    return { isApproved, approve, mutate };
};

export default useApproveToken;
